import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { formatBRL, formatDate } from '@/pages/painel/lib/comercial';
import { TIPOS_FORNECEDOR_LABELS } from '@/pages/painel/lib/inteligencia';

interface Fornecedor {
  id: number;
  empresa_id: number | null;
  tipo_fornecedor: string[];
  marcas: string[];
  produtos: string[];
  regiao: string | null;
  score_fornecedor: number | null;
  status_relacionamento: string;
  empresas: { nome: string; cidade: string | null; uf: string | null };
}

interface ComparacaoRow {
  fornecedor_id: number;
  nome: string;
  cidade: string | null;
  uf: string | null;
  tipo_fornecedor: string[];
  marcas: string[];
  produtos: string[];
  ultimo_preco: number | null;
  ultimo_preco_produto: string | null;
  ultimo_preco_data: string | null;
  desconto: number | null;
  frete: string | null;
  prazo: string | null;
  pedido_minimo: string | null;
  condicao_pagamento: string | null;
  regiao: string | null;
  score_fornecedor: number | null;
}

interface FornecedorComparadorProps {
  fornecedores: Fornecedor[];
  password: string;
  onClose: () => void;
}

export default function FornecedorComparador({ fornecedores, password, onClose }: FornecedorComparadorProps) {
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [produto, setProduto] = useState('');
  const [comparacao, setComparacao] = useState<ComparacaoRow[]>([]);
  const [melhorPreco, setMelhorPreco] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: number) => {
    setSelecionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const comparar = async () => {
    if (selecionados.length < 2) {
      setError('Selecione ao menos 2 fornecedores para comparar.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('filas-admin', {
        body: { action: 'fornecedor_comparar', password, fornecedor_ids: selecionados, produto: produto.trim() || undefined },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      if (data?.error) throw new Error(data.error);
      setComparacao(data?.comparacao ?? []);
      setMelhorPreco(data?.melhor_preco_fornecedor_id ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao comparar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Comparar fornecedores</h3>
            <p className="text-xs text-gray-500">Comparação lado a lado. "Melhor preço" só quando mesmo produto e preços válidos.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              value={produto}
              onChange={(e) => setProduto(e.target.value)}
              placeholder="Filtrar por produto (ex.: cadeira)"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] flex-1 min-w-[200px] bg-white"
            />
            <button
              onClick={comparar}
              disabled={loading || selecionados.length < 2}
              className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {loading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-scales-3-line"></i>}
              Comparar
            </button>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* Seleção */}
          <div className="flex flex-wrap gap-2">
            {fornecedores.map((f) => (
              <button
                key={f.id}
                onClick={() => toggle(f.id)}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
                  selecionados.includes(f.id) ? 'bg-[#E8610A] text-white border-[#E8610A]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#E8610A]/40'
                }`}
              >
                <i className={selecionados.includes(f.id) ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}></i>
                {f.empresas?.nome ?? 'Fornecedor'}
              </button>
            ))}
          </div>

          {/* Tabela comparativa */}
          {comparacao.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-semibold text-gray-500 text-xs">Fornecedor</th>
                    {comparacao.map((c) => (
                      <th key={c.fornecedor_id} className="text-left py-2 px-3 font-semibold text-gray-900 text-xs">
                        {c.nome}
                        {melhorPreco === c.fornecedor_id && (
                          <span className="ml-1 inline-flex items-center text-[10px] text-green-700 bg-green-50 rounded-full px-1.5 py-0.5">melhor preço</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { label: 'Tipo', render: (c: ComparacaoRow) => (c.tipo_fornecedor.map((t) => TIPOS_FORNECEDOR_LABELS[t] ?? t).join(', ') || '—') },
                    { label: 'Cidade', render: (c: ComparacaoRow) => [c.cidade, c.uf].filter(Boolean).join(' - ') || '—' },
                    { label: 'Marcas', render: (c: ComparacaoRow) => c.marcas.join(', ') || '—' },
                    { label: 'Produtos', render: (c: ComparacaoRow) => c.produtos.join(', ') || '—' },
                    { label: 'Último preço', render: (c: ComparacaoRow) => (c.ultimo_preco != null ? `${formatBRL(c.ultimo_preco)}${c.ultimo_preco_produto ? ` (${c.ultimo_preco_produto})` : ''}` : '—') },
                    { label: 'Data do preço', render: (c: ComparacaoRow) => formatDate(c.ultimo_preco_data) },
                    { label: 'Desconto', render: (c: ComparacaoRow) => (c.desconto != null ? `${c.desconto}%` : '—') },
                    { label: 'Frete', render: (c: ComparacaoRow) => c.frete ?? '—' },
                    { label: 'Prazo', render: (c: ComparacaoRow) => c.prazo ?? '—' },
                    { label: 'Pedido mínimo', render: (c: ComparacaoRow) => c.pedido_minimo ?? '—' },
                    { label: 'Condição', render: (c: ComparacaoRow) => c.condicao_pagamento ?? '—' },
                    { label: 'Região', render: (c: ComparacaoRow) => c.regiao ?? '—' },
                    { label: 'Score', render: (c: ComparacaoRow) => (c.score_fornecedor != null ? String(c.score_fornecedor) : '—') },
                  ].map((row) => (
                    <tr key={row.label}>
                      <td className="py-2 px-3 text-gray-500 text-xs whitespace-nowrap font-medium">{row.label}</td>
                      {comparacao.map((c) => (
                        <td key={c.fornecedor_id} className="py-2 px-3 text-gray-800 text-xs">{row.render(c)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}